import React, { useState, useEffect } from "react";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Row,
  Col,
} from "reactstrap";
import { CREATE_NEW_SUPPLIER } from "../../redux/Action/actionTypes";
import { useDispatch } from "react-redux";

// Define the Supplier interface to structure supplier data
export interface Supplier {
  Name: string;
  SupplierLocation?: string | null;
  [key: string]: any; // Allow additional fields if needed
}

// Component for creating or editing a supplier
const NewSupplierForm = ({
  onCancel, // Callback function to handle cancel action
  supplier, // Optional supplier data for edit mode
}: {
  onCancel: () => void;
  supplier?: Supplier | null;
}) => {
  // State variables for supplier fields
  const [id, setId] = useState(supplier?.id || "");
  const [name, setName] = useState(supplier?.Name ?? "");
  const [location, setLocation] = useState(supplier?.SupplierLocation ?? "");
  const [touched, setTouched] = useState({
    name: false, // Tracks if the name field has been interacted with
    location: false, // Tracks if the location field has been interacted with
  });

  // Redux dispatch function to trigger actions
  const dispatch = useDispatch();

  // Effect to update state when the supplier prop changes (useful for edit mode)
  useEffect(() => {
    if (supplier) {
      setId(supplier.value || "");
      setName(supplier.Name ?? "");
      setLocation(supplier.supplierLocation ?? "");
    }
  }, [supplier]);

  // Validation for the name field
  const isNameInvalid =
    name.length > 50 || (touched.name && name.trim() === "");

  // Function to handle saving the supplier data
  const handleSave = () => {
    if (isNameInvalid) return; // Prevent saving if validation fails
    const newSupplier = {
      ID: id || null, // Use null if ID is empty
      Name: name.trim(), // Trim whitespace from name
      SupplierLocation: location.trim() || null, // Use null if location is empty
    };
    // Dispatch action to create or update supplier
    dispatch({ type: CREATE_NEW_SUPPLIER, payload: { newSupplier } });
    onCancel(); // Trigger cancel callback after saving
  };

  // Render the form UI
  return (
    <div className="mb-3 mt-3 p-3 border rounded bg-light">
      <Form>
        <Row>
          <Col md={6}>
            <FormGroup className="mb-2">
              <Label for="name" className="mb-1">
                * Supplier's name:
              </Label>
              <Input
                type="text"
                id="name"
                bsSize="sm"
                value={name}
                onChange={(e) => setName(e.target.value)} // Update name state on input change
                onBlur={() => setTouched((prev) => ({ ...prev, name: true }))} // Mark name field as touched on blur
                invalid={isNameInvalid} // Apply invalid styling if validation fails
              />
              <FormFeedback>
                {name.length > 50 ? "Max 50 characters." : "Required."} {/* Show validation feedback */}
              </FormFeedback>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup className="mb-2">
              <Label for="location" className="mb-1">
                Supplier's location:
              </Label>
              <Input
                type="text"
                id="location"
                bsSize="sm"
                value={location}
                onChange={(e) => setLocation(e.target.value)} // Update location state on input change
                onBlur={() =>
                  setTouched((prev) => ({ ...prev, location: true })) // Mark location field as touched on blur
                }
              />
              <FormFeedback>
                {location.length > 1000 ? "Max 1000 characters." : ""} {/* Show validation feedback */}
              </FormFeedback>
            </FormGroup>
          </Col>
        </Row>
        <div className="d-flex gap-2 mt-2">
          <Button color="secondary" size="sm" onClick={onCancel}>
            Cancel {/* Button to trigger cancel action */}
          </Button>
          <Button
            color="primary"
            size="sm"
            onClick={handleSave} // Button to save supplier data
            disabled={!name.trim() || isNameInvalid} // Disable button if name is invalid
          >
            {supplier ? "Update" : "Save"} {/* Show "Update" for edit mode, "Save" for create mode */}
          </Button>
        </div>
      </Form>
    </div>
  );
};

// Export the component for use in other parts of the application
export default NewSupplierForm;